import { buildFontSelection, findFamily } from "./fonts.js";

export const FALLBACK_TEXT_FONT_FAMILIES = ["Inter", "Montserrat"];
const RECENT_TEXT_LAYER_SAMPLE = 4;

const asFamilyList = (families) =>
  Array.isArray(families) ? families : Object.values(families || {});

const walkTextFontAssetRefs = (nodes, refs) => {
  Object.values(nodes || {}).forEach((node) => {
    if (node?.type === "text") {
      const fontAssetRef = String(node.style?.fontAssetRef || "").trim();
      if (fontAssetRef) refs.push(fontAssetRef);
    }
    if (node?.children) walkTextFontAssetRefs(node.children, refs);
  });
};

export const collectTextFontAssetRefsFromNodes = ({ nodes = {} } = {}) => {
  const refs = [];
  walkTextFontAssetRefs(nodes, refs);
  return refs;
};

export const collectTextFontAssetRefsFromLayers = ({ layers = [] } = {}) =>
  [...(Array.isArray(layers) ? layers : [])]
    .filter((layer) => layer?.type === "text")
    .sort(
      (left, right) => (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0)
    )
    .map((layer) => String(layer.style?.fontAssetRef || "").trim())
    .filter(Boolean);

const mostUsedFontAssetRef = (fontAssetRefs = []) => {
  const sample = fontAssetRefs.filter(Boolean).slice(-RECENT_TEXT_LAYER_SAMPLE);
  if (!sample.length) return "";

  const counts = new Map();
  sample.forEach((name) => {
    counts.set(name, (counts.get(name) || 0) + 1);
  });

  let bestName = "";
  let bestCount = 0;
  counts.forEach((count, name) => {
    if (count < bestCount) return;
    if (count > bestCount || !bestName) {
      bestCount = count;
      bestName = name;
    }
  });
  return bestName;
};

export const resolveDefaultTextFontSelection = ({
  families = [],
  brandFontFamily,
  existingFontAssetRefs = [],
} = {}) => {
  const list = asFamilyList(families);
  if (!list.length) return {};

  const matchFamily = (name) =>
    findFamily({ families: list, fontAssetRef: name });

  const brandFamily = matchFamily(brandFontFamily);
  if (brandFamily) return buildFontSelection({ family: brandFamily });

  const usedFamily = matchFamily(mostUsedFontAssetRef(existingFontAssetRefs));
  if (usedFamily) return buildFontSelection({ family: usedFamily });

  for (const name of FALLBACK_TEXT_FONT_FAMILIES) {
    const family = matchFamily(name);
    if (family) return buildFontSelection({ family });
  }

  return buildFontSelection({ family: list[0] });
};

export const defaultTextStyleFromCatalog = ({
  families = [],
  brandFontFamily,
  existingFontAssetRefs = [],
} = {}) => {
  const selection = resolveDefaultTextFontSelection({
    families,
    brandFontFamily,
    existingFontAssetRefs,
  });
  if (!selection?.fontAssetRef) return {};
  return {
    fontAssetRef: selection.fontAssetRef,
    fontFaceRef: selection.fontFaceRef || "",
    fontWeight: selection.fontWeight ?? 400,
  };
};
